"use client";
import React, { useEffect, useState } from "react";
import { TrendingUp } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../components/ui/chart";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

export const description = "An area chart with gradient fill";

export function CummPNLChart({ cummPnlMerged, assetCummPnl, isWeekly, cummPnlView, setCummPnlView }) {
  const [selectedTab, setSelectedTab] = useState("All");
  const [timeRange, setTimeRange] = useState("all");
  const [filteredData, setFilteredData] = useState([]);
  const assetKeys = ["All", "Nifty", "Bank", "Stock"];
  const assetColors = {
    Nifty: "#1f77b4",
    Bank: "#ff7f0e",
    Stock: "#2ca02c",
    All: "#d72660", // Color for combined line
  };

  const getAssetData = (asset) => assetCummPnl[asset] || [];
  const getCombinedData = () => cummPnlMerged || [];

  // Helper for weekly label
  const getWeeklyLabel = (row) => {
    if (!row.week_start || !row.week_end) return row.week_start || row.date;
    return `${row.week_start} - ${row.week_end}`;
  };

  // Filtering logic for time range
  function filterByTimeRange(data) {
    if (timeRange === "all") return data;
    const now = new Date();
    return data.filter((d) => {
      const dateStr = isWeekly ? d.week_end : d.date;
      const [day, month, year] = dateStr.split("-");
      const date = new Date(`${year}-${month}-${day}`);
      let days = 0;
      if (timeRange === "7d") days = 7;
      else if (timeRange === "15d") days = 15;
      else if (timeRange === "30d") days = 30;
      return now - date < days * 24 * 60 * 60 * 1000;
    });
  }

  // Compute combined line for All (sum of Nifty, Bank, Stock)
  function addCombinedLine(data) {
    return data.map(row => ({
      ...row,
      All: (row.Nifty || 0) + (row.Bank || 0) + (row.Stock || 0)
    }));
  }

  // Helper for Indian number formatting (lakh/crore)
  function formatIndianNumber(num) {
    if (num === undefined || num === null) return '';
    num = Number(num);
    if (Math.abs(num) >= 1e7) return (num / 1e7).toFixed(2) + ' Cr';
    if (Math.abs(num) >= 1e5) return (num / 1e5).toFixed(2) + ' L';
    return num.toLocaleString('en-IN');
  }

  useEffect(() => {
    let data = selectedTab === "All" ? getCombinedData() : getAssetData(selectedTab);
    let filtered = filterByTimeRange(data);
    if (selectedTab === "All") {
      filtered = addCombinedLine(filtered);
    }
    setFilteredData(filtered);
  }, [selectedTab, cummPnlMerged, assetCummPnl, timeRange, isWeekly]);

  const CustomTooltipContent = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-black border border-gray-700 rounded p-3 shadow-lg">
        <p className="text-gray-300 mb-2">
          {label ? (() => {
            if (!isWeekly) {
              const [day, month, year] = label.split("-");
              const dateObj = new Date(`${year}-${month}-${day}`);
              return dateObj.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric"
              });
            } else {
              return label;
            }
          })() : ""}
        </p>
        <div className="space-y-1">
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: assetColors[entry.name] || entry.stroke }}
              />
              <span className="text-gray-300">{entry.name}:</span>
              <span className="text-white font-medium">
                {formatIndianNumber(entry.value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Cummlative PNL Chart</CardTitle>
            <CardDescription>
              {selectedTab === "All"
                ? `Comparison of Cumulative PNL for Nifty, Bank, Stock, and All (${isWeekly ? "Weekly" : "Daily"})`
                : `Showing Cumulative PNL for the last ${filteredData?.length} ${isWeekly ? "weeks" : "days"}`}
            </CardDescription>
          </div>
          <div className="flex flex-row items-center gap-4 ml-4">
            <Tabs value={selectedTab} onValueChange={setSelectedTab}>
              <TabsList>
                {assetKeys.map((asset) => (
                  <TabsTrigger key={asset} value={asset}>{asset}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            {/* Time range dropdown */}
            <select
              value={timeRange}
              onChange={e => setTimeRange(e.target.value)}
              className="bg-black text-white border border-gray-700 rounded px-2 py-1"
            >
              <option value="all">ALL</option>
              <option value="7d">7 days</option>
              <option value="15d">15 days</option>
              <option value="30d">30 days</option>
            </select>
            <span className="font-semibold text-white/80">View:</span>
            <select
              value={cummPnlView}
              onChange={e => setCummPnlView(e.target.value)}
              className="bg-black text-white border border-gray-700 rounded px-2 py-1"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredData.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">No data available for this selection.</div>
        ) : (
          <ChartContainer className="h-[450px] w-full" config={{}}>
            {selectedTab === "All" ? (
              <LineChart data={filteredData} margin={{ left: 60, right: 12 }}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey={isWeekly ? "week_start" : "date"}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={12}
                  tickFormatter={(value, idx) => {
                    if (!isWeekly) {
                      if (!value) return "";
                      const parts = typeof value === "string" ? value.split("-") : [];
                      if (parts.length !== 3) return value;
                      const [day, month, year] = parts;
                      const dateObj = new Date(`${year}-${month}-${day}`);
                      return dateObj.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
                    } else {
                      const row = filteredData[idx];
                      return getWeeklyLabel(row);
                    }
                  }}
                  interval={Math.ceil(filteredData.length / 8)}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={1}
                  tickCount={11}
                  domain={["dataMin-200000", "dataMax+100000"]}
                  tickFormatter={formatIndianNumber}
                />
                <Tooltip
                  content={<CustomTooltipContent />}
                />
                <Line type="monotone" dataKey="Nifty" stroke={assetColors.Nifty} strokeWidth={3} dot={false} name="Nifty" connectNulls={true} />
                <Line type="monotone" dataKey="Bank" stroke={assetColors.Bank} strokeWidth={3} dot={false} name="Bank" connectNulls={true} />
                <Line type="monotone" dataKey="Stock" stroke={assetColors.Stock} strokeWidth={3} dot={false} name="Stock" connectNulls={true} />
                <Line type="monotone" dataKey="All" stroke={assetColors.All} strokeWidth={3} dot={false} name="All" connectNulls={true} />
              </LineChart>
            ) : (
              <AreaChart data={filteredData} margin={{ left: 60, right: 12 }}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey={isWeekly ? "week_start" : "date"}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={12}
                  tickFormatter={(value, idx) => {
                    if (!isWeekly) {
                      if (!value) return "";
                      const parts = typeof value === "string" ? value.split("-") : [];
                      if (parts.length !== 3) return value;
                      const [day, month, year] = parts;
                      const dateObj = new Date(`${year}-${month}-${day}`);
                      return dateObj.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
                    } else {
                      const row = filteredData[idx];
                      return getWeeklyLabel(row);
                    }
                  }}
                  interval={Math.ceil(filteredData.length / 8)}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={1}
                  tickCount={11}
                  domain={["dataMin-200000", "dataMax+100000"]}
                  tickFormatter={formatIndianNumber}
                />
                <Tooltip
                  content={<CustomTooltipContent />}
                />
                <defs>
                  <linearGradient id="fillPnl" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={assetColors[selectedTab]} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={assetColors[selectedTab]} stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <Area 
                  dataKey="cumulative_pnl" 
                  type="natural" 
                  fill="url(#fillPnl)" 
                  fillOpacity={0.4} 
                  stroke={assetColors[selectedTab]} 
                  strokeWidth={3} 
                  dot={false}
                  name={selectedTab}
                />
              </AreaChart>
            )}
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
